import find from 'lodash/find'
import map from 'lodash/map'
import React, { Dispatch, forwardRef, SetStateAction, useContext, useEffect, useState } from 'react'

import { useSavedCheckoutLocalData } from '@Domains/Checkout/Hooks/useSavedCheckoutLocalData'
import { PaymentIcon } from '@Domains/Checkout/Steps/Payment/Components/PaymentIconFactory/PaymentIconFactory'
import {
  ByJunoFormData,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Default/Components/ByJuno/ByJuno'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'
import { CheckoutV2PaymentMethod } from '@Pages/onestepcheckout'
import { FormValidationAction } from '@Shared/Components/Forms/FormElement.types'
import { FormInputDeprecated } from '@Shared/Components/Forms/FormElements/FormInputDeprecated'
import { FormSelectDeprecated } from '@Shared/Components/Forms/FormElements/FormSelectDeprecated'
import { FormGroupDeprecated } from '@Shared/Components/Forms/FormGroups/FormGroupDeprecated'
import { BetweenValuesValidation } from '@Shared/Components/Forms/Validations/BetweenValuesValidation'
import { MaxLengthValidation } from '@Shared/Components/Forms/Validations/MaxLengthValidation'
import { MinLengthValidation } from '@Shared/Components/Forms/Validations/MinLengthValidation'
import { NotEmptyValidation } from '@Shared/Components/Forms/Validations/NotEmptyValidation'
import { RoundCheckbox } from '@Shared/Components/Inputs/Checkbox/RoundCheckbox'

import styles from './ByJuno.module.scss'

interface ByJunoFormProps {
  formData: ByJunoFormData
  formValid: boolean
  option: CheckoutV2PaymentMethod
  setFormData: Dispatch<SetStateAction<ByJunoFormData>>
  setFormValid: Dispatch<SetStateAction<boolean>>
}

interface Gender {
  name: string
  value: string
}

export const ByJunoForm = forwardRef<HTMLFormElement, ByJunoFormProps>(({
  formData,
  formValid,
  option,
  setFormData,
  setFormValid,
}, ref) => {
  const { store } = useContext(MetadataContext)
  const { checkoutLocal, saveCheckoutLocal } = useSavedCheckoutLocalData()

  const [loadedLocalData, setLoadedLocalData] = useState(false)

  const [submitAttempted, setSubmitAttempted] = useState(false)

  useEffect(() => {
    if (!loadedLocalData && checkoutLocal) {
      if (checkoutLocal.byJunoData) {
        setFormData({ ...checkoutLocal.byJunoData })
      }
      setLoadedLocalData(true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutLocal])

  useEffect(() => {
    if (loadedLocalData) {
      saveCheckoutLocal({
        byJunoData: formData,
      })
    }
  }, [formData, loadedLocalData, saveCheckoutLocal])

  const { t } = useTranslation()

  const genders: Gender[] = [
    {
      value: '1',
      name: t('checkout:COF_MALE'),
    },
    {
      value: '2',
      name: t('checkout:COF_FEMALE'),
    },
  ]

  const handleInvoiceTypeChange = (type: string) => {
    setFormData({
      ...formData,
      invoiceType: type,
    })
  }

  return (
    <FormGroupDeprecated
      id="byJunoForm"
      model={formData}
      onBlur={(model, valid) => {
        setFormValid(valid)
      }}
      onSubmit={(model, valid) => {
        setSubmitAttempted(true)
        setFormValid(valid)
      }}
      ref={ref}
      updateModel={setFormData}
      validateOn={FormValidationAction.BLUR}
    >
      <div className={styles.genderGroup}>
        <span className={styles.paymentIcon}>
          <PaymentIcon name={option.type} url={option.logo?.url || ''} />
        </span>

        <FormSelectDeprecated
          childElement={(value: string) => find(genders, gender => gender.value === value)?.name}
          className={styles.genderSelect}
          errorClassName="checkout-shipping-error"
          id="cof_by_juno_gender"
          label={`${t('checkout:COF_GENDER')} *`}
          modelKey="gender"
          options={map(genders, gender => gender.value)}
          showValidation
          triggersValidation={[formData.gender]}
          validations={[NotEmptyValidation]}
        />
      </div>

      <p className={styles.birthDayLabel}>{t('checkout:COF_DATE_OF_BIRTH')}</p>

      <div className={styles.birthDayGroup}>
        <div className={styles.birthDayInputGroup}>
          <FormInputDeprecated
            errorClassName="checkout-shipping-error"
            label={`${t('checkout:COF_DAY')} *`}
            maxLength={2}
            modelKey="day"
            triggersValidation={[formData.day]}
            validations={[MinLengthValidation(2), NotEmptyValidation, MaxLengthValidation(2), BetweenValuesValidation(1, 31)]}
          />
          <span className={styles.birthDayInputLabel}>
            {t('checkout:COF_DD')}
            {' '}
            (01)
          </span>
        </div>
        <div className={styles.birthDayInputGroup}>
          <FormInputDeprecated
            errorClassName="checkout-shipping-error"
            label={`${t('checkout:COF_MONTH')} *`}
            maxLength={2}
            modelKey="month"
            triggersValidation={[formData.month]}
            validations={[MinLengthValidation(2), NotEmptyValidation, MaxLengthValidation(2), BetweenValuesValidation(1, 12)]}
          />
          <span className={styles.birthDayInputLabel}>
            {t('checkout:COF_MM')}
            {' '}
            (01)
          </span>
        </div>
        <div className={styles.birthDayInputGroup}>
          <FormInputDeprecated
            errorClassName="checkout-shipping-error"
            label={`${t('checkout:COF_YEAR')} *`}
            maxLength={4}
            modelKey="year"
            triggersValidation={[formData.year]}
            validations={[NotEmptyValidation, BetweenValuesValidation(1900, new Date().getFullYear())]}
          />
          <span className={styles.birthDayInputLabel}>
            {t('checkout:COF_YYYY')}
            {' '}
            (1999)
          </span>
        </div>
      </div>
      <div className={styles.invoiceTypeWrapper}>
        <RoundCheckbox checked={formData.invoiceType === 'email'} id="byjuno-email" onCheck={() => handleInvoiceTypeChange('email')} />
        <button onClick={() => handleInvoiceTypeChange('email')} type="button">
          {store.id === '19' ? 'Rechnungsversand via E-Mail (ohne Gebühr)' : 'Facture par e-mail (gratuite)'}
        </button>
      </div>
      <div className={styles.invoiceTypeWrapper}>
        <RoundCheckbox checked={formData.invoiceType === 'postal'} id="byjuno-postal" onCheck={() => handleInvoiceTypeChange('postal')} />
        <button onClick={() => handleInvoiceTypeChange('postal')} type="button">
          {store.id === '19'
            ? 'Rechnungsversand in Papierform via Post (gegen Gebühr von CHF 3.50)'
            : 'Facture au format papier, expédiée par voie postale (supplément de CHF 3.50)'}
        </button>
      </div>

      {submitAttempted && !formValid && <div className={styles.byjunoFormError}>{t('checkout:COF_FILL_OUT_ALL_FIELDS_CORRECTLY')}</div>}

    </FormGroupDeprecated>
  )
})
